"use client";

import { Trans } from "@lingui/react/macro";
import { AlertCircleIcon, Loader2Icon } from "lucide-react";
import { useActionState, useId, useState } from "react";
import { useServerActionErrorHandler } from "@/app/[lang]/_over-capacity";
import { createDashboard } from "@/app/[lang]/[workspaceId]/action";
import { useTrackEvent } from "@/components/analytics/events";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { isDemoMode } from "@/config";
import { useRouter } from "@/lib/navigation";
import { useRefreshWorkspaceSidebar } from "../../_contexts/workspace-sidebar-data-context";

type Props = {
  workspaceId: string;
  trigger: React.ReactNode;
};

export default function CreateDashboardDialog({ workspaceId, trigger }: Props) {
  const id = useId();
  const router = useRouter();
  const trackEvent = useTrackEvent();
  const { handleServerActionError } = useServerActionErrorHandler();
  const [isOpen, setIsOpen] = useState(false);
  const refreshWorkspaceSidebar = useRefreshWorkspaceSidebar();

  const [state, action, isPending] = useActionState<
    { error?: string; dashboardId?: string },
    FormData
  >(async (_, formData: FormData) => {
    const name = formData.get("name");
    if (!name) {
      return { error: "Name is required" };
    }
    if (typeof name !== "string") {
      return { error: "Name must be a string" };
    }

    const result = await createDashboard(workspaceId, name);

    // Over Capacityエラーをチェックしてモーダル表示
    if (result.error) {
      handleServerActionError(result);
      return result;
    }

    if (result.dashboardId) {
      setIsOpen(false);
      trackEvent({
        eventName: "dashboard_created",
        payload: {
          product_area: "dashboard",
        },
      });
      router.push(
        `/${encodeURIComponent(workspaceId)}/dashboards/${encodeURIComponent(
          result.dashboardId,
        )}`,
      );
      router.refresh();
      refreshWorkspaceSidebar("dashboard-mutation");
    }

    return result;
  }, {});

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Create Dashboard</Trans>
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <Trans>
            Create a new dashboard to track your projects' performance.
          </Trans>
        </DialogDescription>
        <form action={action} className="flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor={`${id}-name`}>
              <Trans>Name</Trans>
            </Label>
            <Input id={`${id}-name`} name="name" />
          </div>
          {state.error && (
            <Alert variant="destructive">
              <AlertCircleIcon className="w-4 h-4" />
              <AlertTitle>
                <Trans>Error</Trans>
              </AlertTitle>
              <AlertDescription>{state.error}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button type="submit" disabled={isPending || isDemoMode}>
              {isPending ? (
                <Loader2Icon className="w-4 h-4 animate-spin" />
              ) : (
                <Trans>Create</Trans>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
